import { defineComponent, PropType } from "vue";
import { Container } from "aurelia-dependency-injection";

import {
    IGetThreadMessagesByIdRequest,
    IThreadDetails,
    IThreadMessage,
    IThreadReplyRequest
} from "../../../../../resources-vue/vue-interfaces/messaging-service/i-family-portal";
import {
    formatDateTimeToLocal,
    formatDateToLocalBasedOnDayMode,
    formatDisplayUsers,
    formatUserInitials
} from "../../../../../common/vue-helpers/modifiers/format-message";
import { getSolutionClassIndex } from "../../../../../common/vue-helpers/solution-color-index";
import { markdownToHtml } from "../../../../../common/vue-helpers/modifiers/markdown-to-html-modifier";
import { sanitizeMarkup } from "../../../../../common/vue-helpers/modifiers/value-modifier";
import { ThreadStatus, ThreadStatusEnum } from "../../../../../enums/enums";
import { FamilyPortalService } from "../../../../../services/family-portal-service";
import { AnalyticsCategory, AnalyticsService } from "../../../../../services/analytics.service";
import MessageInputHandler from "../../../../../resources-vue/vue-custom-elements/MessagingService/MessageInputHandler/MessageInputHandler.vue";

export default defineComponent({
    name: "FPThreadsCard",
    components: {
        MessageInputHandler
    },
    props: {
        thread: {
            type: Object as PropType<IThreadDetails>,
            required: true
        },
        patientAgencyName: {
            type: String,
            required: true
        },
        canEditPatient: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        remainingRepliesCount(): number {
            if (!this.thread?.messages || !this.thread?.totalMessagesCount) {
                return 0;
            }

            return this.thread.totalMessagesCount - this.thread.messages.length;
        },

        showLoadMoreReplies(): boolean {
            return this.remainingRepliesCount > 0;
        }
    },
    watch: {
        "thread.messages": {
            handler() {
                this.scrollToBottom();
            },
            deep: true
        },
        showReply(newValue: boolean) {
            if (newValue) {
                this.scrollToBottom();
            }
        }
    },
    mounted() {
        this.scrollToBottom();
    },
    data() {
        return {
            showReply: false as boolean,
            formatDisplayUsers,
            getSolutionClassIndex,
            formatUserInitials,
            formatDateTimeToLocal,
            formatDateToLocalBasedOnDayMode,
            markdownToHtml,
            sanitizeMarkup,
            ThreadStatus,
            ThreadStatusEnum,
            _familyPortalService: null as FamilyPortalService,
            _analyticsService: null,
            userOs: ""
        };
    },
    created() {
        this._familyPortalService = Container.instance.get(FamilyPortalService);
        this._analyticsService = Container.instance.get(AnalyticsService);
        this.userOs = window.sessionStorage.getItem("userOs");
    },
    methods: {
        getSenderMeta(message: IThreadMessage) {
            return message.senderUser;
        },
        handleshowReply() {
            this._analyticsService.logEvent({
                category: AnalyticsCategory.FamilyPortal,
                action: "Reply to thread"
            });
            this.showReply = true;
        },
        async sendMessage(msg: any) {
            const query: IThreadReplyRequest = {
                body: msg.message,
                conversationThreadId: this.thread.id
            };
            this._analyticsService.logEvent({
                category: AnalyticsCategory.FamilyPortal,
                action: "Reply to thread-Send Message"
            });

            await this._familyPortalService.replyOnThread(query);

            // close input after successful send
            this.showReply = false;
        },
        async loadMoreMessages() {
            try {
                const query: IGetThreadMessagesByIdRequest = {
                    fromMessageId: this.thread.messages[0].id,
                    pageLength: 10
                };
                const response = await this._familyPortalService.getThreadMessagesById(this.thread.id, query);
                this.thread.messages = [...response.messages, ...this.thread.messages];
            } catch (error) {
                console.error("Failed to load more messages", error);
            }
        },

        async handleResolve() {
            try {
                await this._familyPortalService.resolveThread(this.thread.id);
                this.thread.conversationThreadStatus = ThreadStatusEnum.Resolved;
                this._analyticsService.logEvent({
                    category: AnalyticsCategory.FamilyPortal,
                    action: "Mark as Resolved"
                });
            } catch (error) {
                console.error(error, "error");
            }
        },

        scrollToBottom() {
            setTimeout(() => {
                const threadMessages = this.$refs.threadMessages as HTMLElement;
                if (threadMessages) {
                    threadMessages.scrollTo({ top: threadMessages.scrollHeight, behavior: "smooth" });
                }
            }, 300);
        },
        toggleImportant() {
            // TODO: future implementation for important message
            console.log();
        }
    }
});
